import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SharedForm from "@widgets/SharedForm";
import SearchPanel from "@widgets/SearchPanel";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnButton from "@shared/components/SnButton";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnFileHelper from "@shared/components/SnFileHelper";

interface Props {
  Menuprops: menuInfo;
}

// 설비등록
const SN_PRD_BAS_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(), // 설비정보
    grid2: React.createRef<SnGridRef>(), // 설비점검이력
    grid3: React.createRef<SnGridRef>(), // 설비수리이력
    grid4: React.createRef<SnGridRef>(), // 설비Jig
    grid5: React.createRef<SnGridRef>(), // 생산기술이력
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const SharedFormHref = useRef<any>(null);
  const fileHelperRef = useRef<any>(null);
  const [focusedRowDataH, setFocusedRowDataH] = useState<any>();
  const { userInfo } = global(); // Login user 정보
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [sharedFormH, setSharedFormH] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
  });
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));

        setSharedFormH(rtn.form.freeform1);

        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
          grid5: rtn.grid.grid5,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsJ = getGridCurrent("grid4").getChange();
    const modifiedRowsT = getGridCurrent("grid5").getChange();

    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsJ.modifiedRowcount > 0 ||
      modifiedRowsT.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_prd_bas_reg_002_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      getGridCurrent("grid5").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
        grid4: data[3] || [],
        grid5: data[4] || [],
      });

      setFocusedRowDataH(null);
    } catch (error: any) {}
  };
  //#endregion

  const onInsert = () => {
    try {
      getGridCurrent("grid1").addRow();
    } catch (error: any) {}
  };

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    getGridCurrent("grid5").deleteAllRows();
    getGridCurrent("grid4").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      getGridCurrent("grid5").resetUpdate();
      return;
    }

    return await onSave();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid4").stopEditing();
      getGridCurrent("grid5").stopEditing();

      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // Jig 필수값 체크
      const requiredValuesCheckJ =
        getGridCurrent("grid4").requiredValuesCheck();
      if (!requiredValuesCheckJ) {
        return false;
      }

      // 생산기술이력 필수값 체크
      const requiredValuesCheckT =
        getGridCurrent("grid5").requiredValuesCheck();
      if (!requiredValuesCheckT) {
        return false;
      }

      const equipChanges = getGridCurrent("grid1").getChange();
      const equipJigChanges = getGridCurrent("grid4").getChange();
      const equipTechniqueChanges = getGridCurrent("grid5").getChange();

      if (
        equipChanges.modifiedRowcount === 0 &&
        equipJigChanges.modifiedRowcount === 0 &&
        equipTechniqueChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 요청 데이터 구성
      const SaveData = {
        prd_equip: equipChanges,
        prd_equip_jig: equipJigChanges,
        prd_equip_technique: equipTechniqueChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_PRD_BAS_REG_002_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      getGridCurrent("grid5").resetUpdate();

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const visibleIdx = focusedRowNodeH?.rowIndex || 0;
      getGridCurrent("grid1").clearFocus();
      getGridCurrent("grid1").selectAndFocusRow(visibleIdx);

      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      getGridCurrent("grid5").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        am_pur: "0",
        qt_pur: "0",
        rt_capa_plan: "0",
        tp_equip: "line",
        tp_insp_pid: "0",
        yn_use: "Y",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      setFocusedRowDataH(selectedRow);
    } else {
      setFocusedRowDataH(null);
    }
  };

  // 상세정보 파일 버튼 (파일서버 연동 전까지 동작 보류)
  const onGridHBtnsClick = async (id: string) => {
    // const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    // if (!focusedRowNodeH) {
    //   await showDialog("alter", "행을 선택하세요.");
    //   return;
    // }
    // const customPath = `equip/${focusedRowNodeH.data.cd_equip}/`;
    // if (id == "btnDownload") {
    //   fileHelperRef.current?.downloadFile(focusedRowNodeH.data.nm_file, customPath);
    // }
    // if (id === "btnDelete") {
    //   fileHelperRef.current?.deleteFile(focusedRowNodeH.data.nm_file, customPath);
    // }
    // if (id === "btnUpload") {
    //   fileHelperRef.current?.openFileDialog(customPath);
    // }
  };

  const handleFileUploadSuccess = async (
    fileName: string,
    isUpload: boolean
  ) => {
    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      await showDialog("alter", "먼저 행을 선택하세요.");
      return;
    }

    focusedRowNodeH.setDataValue("dc_url_pic", fileName);
  };
  //#endregion

  // 하위 그리드 행 추가 전 상위(설비) 행 선택/필수값 확인
  const checkGridH = (): boolean => {
    getGridCurrent("grid1").stopEditing();

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (focusedRowNodeH == null) {
      showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    const requiredValuesCheckH = getGridCurrent("grid1").requiredValuesCheck();
    if (!requiredValuesCheckH) {
      return false;
    }

    return true;
  };

  //#region GridJ(설비Jig) Events
  const onBeforeAddRowJ = (): any => {
    try {
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        cd_equip: focusedRowNodeH.data?.cd_equip,
        am_tool: "0.00",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGridJBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        if (!checkGridH()) return;
        getGridCurrent("grid4").addRow();
        break;
      case "deleteRow":
        if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
          return;
        }

        getGridCurrent("grid4").deleteSelectedRows();
        break;
    }
  };
  //#endregion

  //#region GridT(생산기술이력) Events
  const onBeforeAddRowT = (): any => {
    try {
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const maxNoLine = getGridCurrent("grid5").getMaxValue("no_line");

      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        cd_equip: focusedRowNodeH.data?.cd_equip,
        no_line: maxNoLine + 1,
        am_repair: "0",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGridTBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        if (!checkGridH()) return;
        getGridCurrent("grid5").addRow();
        break;
      case "deleteRow":
        if (getGridCurrent("grid5").getDisplayedRowCount() < 1) {
          return;
        }

        getGridCurrent("grid5").deleteSelectedRows();
        break;
    }
  };
  //#endregion

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {},
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {},
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onBeforeAddRow: onBeforeAddRowJ,
          onBtnAddClick: onGridJBtnsClick,
          onBtnDeleteClick: onGridJBtnsClick,
        },
      },
      grid5: {
        columns: columns.grid5,
        dataSource: dataSources.grid5,
        events: {
          onBeforeAddRow: onBeforeAddRowT,
          onBtnAddClick: onGridTBtnsClick,
          onBtnDeleteClick: onGridTBtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="22.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "설비정보" }}
            events={grids.grid1.events}
            filter={{
              refs: [
                gridRefs.current["grid2"],
                gridRefs.current["grid3"],
                gridRefs.current["grid4"],
                gridRefs.current["grid5"],
              ],
              columns: ["cd_equip"],
            }}
          />
        </SnResizeBar>

        <SnResizeBar direction={"width"} id="panel2" orgSize="77.5%">
          <div className="flex flex-col h-full min-h-0 space-y-1.5">
            <SnResizeBar direction={"height"} id="panel3" orgSize="50%">
              <SharedForm
                ref={SharedFormHref}
                data={focusedRowDataH}
                controls={sharedFormH}
                gridref={gridRefs.current["grid1"]}
                title="상세정보"
                header={
                  <div className="flex justify-end space-x-1">
                    <SnButton
                      text={"파일다운"}
                      onClick={() => onGridHBtnsClick("btnDownload")}
                    />
                    <SnButton
                      text={"파일삭제"}
                      onClick={() => onGridHBtnsClick("btnDelete")}
                    />
                    <SnButton
                      text={"파일첨부"}
                      onClick={() => onGridHBtnsClick("btnUpload")}
                    />
                  </div>
                }
              />
              <SnFileHelper
                ref={fileHelperRef}
                onFileUploadSuccess={handleFileUploadSuccess}
                accept="image/*"
              />
            </SnResizeBar>
            <div className="flex-1 min-h-0">
              <SnTab
                titles={[
                  "설비점검이력",
                  "설비수리이력",
                  "설비Jig",
                  "생산기술이력",
                ]}
                width={"100%"}
                header={[
                  <SnTabHeader />,
                  <SnTabHeader />,
                  <SnTabHeader
                    onBtnAddClick={onGridJBtnsClick}
                    onBtnDeleteClick={onGridJBtnsClick}
                  />,
                  <SnTabHeader
                    onBtnAddClick={onGridTBtnsClick}
                    onBtnDeleteClick={onGridTBtnsClick}
                  />,
                ]}
                body={[
                  <SnGrid
                    ref={gridRefs.current["grid2"]} // Grid Name
                    columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                    rowData={grids.grid2.dataSource} // Datasource
                    etcProps={{ rowSelection: "single" }}
                    events={grids.grid2.events}
                  />,
                  <SnGrid
                    ref={gridRefs.current["grid3"]} // Grid Name
                    columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                    rowData={grids.grid3.dataSource} // Datasource
                    etcProps={{ rowSelection: "single" }}
                    events={grids.grid3.events}
                  />,
                  <SnGrid
                    ref={gridRefs.current["grid4"]} // Grid Name
                    columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                    rowData={grids.grid4.dataSource} // Datasource
                    etcProps={{ rowSelection: "single" }}
                    events={grids.grid4.events}
                  />,
                  <SnGrid
                    ref={gridRefs.current["grid5"]} // Grid Name
                    columnConfig={grids.grid5.columns} // 컬럼 설정 전달
                    rowData={grids.grid5.dataSource} // Datasource
                    etcProps={{ rowSelection: "single" }}
                    events={grids.grid5.events}
                  />,
                ]}
              />
            </div>
          </div>
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_PRD_BAS_REG_002;
